import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './Header.css';

const Header = ({ title, subtitle, onAddTransaction, onToggleDarkTheme, isDark, onOpenCategories, onOpenGoals,
  onOpenBudgets,
  onOpenBusiness,
  onOpenCards,
  onOpenReports,
  onOpenHelp,
  onExport,
  onImport,
  onOpenProfile,
  vision,
  setVision
}) => {
  const { user, isAuthenticated, logout, userAccounts, setActiveAccount } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const menuRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAction = (action) => {
    action();
    setShowMenu(false);
  };

  const getInitials = (name, email) => {
    if (name) return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    return email ? email[0].toUpperCase() : '?';
  };

  // Gera nome curto para exibição no header (NÃO salva no banco)
  // Regras: ignora preposições, máx 2 palavras principais, sem espaço, máx 18 chars
  const formatDisplayName = (name) => {
    if (!name) return '';
    const stopWords = new Set(['de', 'da', 'do', 'dos', 'das', 'e', 'a', 'o', 'em', 'no', 'na', 'nos', 'nas']);
    const words = name.trim().split(/\s+/);
    const main = words.filter(w => !stopWords.has(w.toLowerCase()));
    // fallback: se todas forem stop words, usa as palavras originais
    return (main.length > 0 ? main : words).slice(0, 2).join('').substring(0, 18);
  };

  return (
    <>
      <header className="header">
        <div className="header-inner">
          <div className="header-left">
            <div className="header-logo">
              <img src="/favicon.png" alt="Logo" />
            </div>
            <div className="header-title-group">
              <h1 className="header-title">{title}</h1>
              <p className="header-subtitle">{subtitle}</p>
            </div>
          </div>

          <div className="header-right">
            <div className="header-desktop-actions">
              {isAuthenticated && (
                <>
                  {/* Botão de Nova Transação */}
                  <button className="header-btn header-btn-primary" onClick={onAddTransaction}>
                    + Nova Transação
                  </button>

                  <div className="menu-wrapper" ref={menuRef}>
                    <button className="header-btn header-btn-secondary" onClick={() => setShowMenu(!showMenu)} title="Menu Principal">
                      <span>☰</span> <span>Menu</span>
                    </button>
                    {showMenu && (
                      <div className="header-dropdown-menu">
                        <div className="dropdown-title">Ações</div>
                        <button className="dropdown-item" onClick={() => handleAction(onOpenGoals)}><span>🎯</span> Metas</button>
                        <button className="dropdown-item" onClick={() => handleAction(onOpenBudgets)}><span>💰</span> Orçamentos</button>
                        <button className="dropdown-item" onClick={() => handleAction(onOpenReports)}><span>📊</span> Relatórios</button>
                        <div className="dropdown-divider"></div>
                        <div className="dropdown-title">Cadastros</div>
                        <button className="dropdown-item" onClick={() => handleAction(onOpenCategories)}><span>🏷️</span> Categorias</button>
                        <button className="dropdown-item" onClick={() => handleAction(onOpenBusiness)}><span>🏢</span> Conta Business</button>
                        <button className="dropdown-item" onClick={() => handleAction(onOpenCards)}><span>💳</span> Meus Cartões</button>
                        <div className="dropdown-divider"></div>
                        <div className="dropdown-title">Importação / Exportação</div>
                        <button className="dropdown-item" onClick={() => handleAction(onImport)}><span>📥</span> Importar CSV</button>
                        <button className="dropdown-item" onClick={() => handleAction(() => onExport('csv'))}><span>📄</span> Extrato CSV</button>
                        <button className="dropdown-item" onClick={() => handleAction(() => onExport('pdf'))}><span>📑</span> Extrato PDF</button>
                        <div className="dropdown-divider"></div>
                        <button className="dropdown-item" onClick={() => handleAction(onOpenHelp)}><span>❓</span> Central de Ajuda</button>
                        <div className="dropdown-divider"></div>
                        <button className="dropdown-item logout-item" onClick={() => handleAction(logout)}><span>🚪</span> Sair do Sistema</button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Toggle de Tema */}
              <button className="header-btn header-btn-icon" onClick={onToggleDarkTheme} title="Alternar tema">
                {isDark ? '☀️' : '🌙'}
              </button>
            </div>

            {/* Avatar e Conta (Desktop) */}
            {isAuthenticated && (
              <div className="header-user-section">
                <div className="header-account-info">
                  <span className="account-label">Conta:</span>
                  <div className="header-vision-wrapper">
                    <select className="header-vision-select" value={vision} onChange={(e) => {
                      const val = e.target.value;
                      setVision(val);
                      if (val.startsWith('business:')) {
                        setActiveAccount(val.split(':')[1]);
                      } else {
                        setActiveAccount(null);
                      }
                    }}>
                      <option value="personal">{formatDisplayName(user?.name || 'Pessoal')}</option>
                      {(userAccounts || []).length > 0 ? (
                        (userAccounts || []).map(acc => (
                           <option key={acc.account_id} value={`business:${acc.account_id}`}>
                             {formatDisplayName(acc.name || 'Business')}
                           </option>
                        ))
                      ) : (
                        user?.business_id && (
                          <option value={`business:${user.business_id}`}>{formatDisplayName(user.nome_business || 'Business')}</option>
                        )
                      )}
                    </select>
                    <span className="vision-arrow">⌄</span>
                  </div>
                </div>
                <div className="header-avatar-circle" title="Minha conta" onClick={onOpenProfile}>
                  {getInitials(user?.name, user?.email)}
                </div>
              </div>
            )}

            {/* Ações Mobile Rápidas */}
            {isAuthenticated && (
              <div className="header-mobile-actions">
                <button className="header-btn-mobile-plus" onClick={onAddTransaction} title="Nova Transação">
                  +
                </button>
                <button className="header-hamburger-mobile" onClick={() => setShowMobileMenu(!showMobileMenu)}>
                  ☰
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Menu Mobile Lateral */}
        {isAuthenticated && showMobileMenu && (
          <div className="mobile-menu-overlay" onClick={() => setShowMobileMenu(false)}>
            <div className="mobile-menu" onClick={e => e.stopPropagation()}>
              
              <div className="mobile-menu-user" onClick={() => { onOpenProfile(); setShowMobileMenu(false); }} style={{ cursor: 'pointer' }}>
                <div className="header-avatar-circle">{getInitials(user?.name, user?.email)}</div>
                <div className="mobile-user-details">
                  <strong>{formatDisplayName(user?.name || '')} ⚙️</strong>
                  <span>{user?.email}</span>
                </div>
              </div>

              {/* Seletor de Visão no Mobile */}
              <div className="mobile-vision-selector">
                <label>Visualizando:</label>
                <select value={vision} onChange={(e) => {
                  const val = e.target.value;
                  setVision(val);
                  if (val.startsWith('business:')) {
                    setActiveAccount(val.split(':')[1]);
                  } else {
                    setActiveAccount(null);
                  }
                  setShowMobileMenu(false);
                }}>
                  <option value="personal">👤 {formatDisplayName(user?.name || 'Pessoal')}</option>
                  {(userAccounts || []).length > 0 ? (
                    (userAccounts || []).map(acc => (
                       <option key={acc.account_id} value={`business:${acc.account_id}`}>
                         🏢 {formatDisplayName(acc.name || 'Business')}
                       </option>
                    ))
                  ) : (
                    user?.business_id && (
                      <option value={`business:${user.business_id}`}>🏢 {formatDisplayName(user.nome_business || 'Business')}</option>
                    )
                  )}
                </select>
              </div>

              <div className="mobile-menu-items">
                <div className="mobile-menu-section-title">Ações</div>
                <button onClick={() => { onAddTransaction(); setShowMobileMenu(false); }}>➕ Nova Transação</button>
                <button onClick={() => { onOpenGoals(); setShowMobileMenu(false); }}>🎯 Minhas Metas</button>
                <button onClick={() => { onOpenBudgets(); setShowMobileMenu(false); }}>💰 Orçamentos</button>
                <button onClick={() => { onOpenReports(); setShowMobileMenu(false); }}>📊 Relatórios</button>
                
                <div className="mobile-menu-section-title">Cadastros</div>
                <button onClick={() => { onOpenCategories(); setShowMobileMenu(false); }}>🏷️ Categorias</button>
                <button onClick={() => { onOpenBusiness(); setShowMobileMenu(false); }}>🏢 Conta Business</button>
                <button onClick={() => { onOpenCards(); setShowMobileMenu(false); }}>💳 Meus Cartões</button>

                <div className="mobile-menu-section-title">Dados</div>
                <button onClick={() => { onImport(); setShowMobileMenu(false); }}>📥 Importar CSV</button>
                <button onClick={() => { onExport('csv'); setShowMobileMenu(false); }}>📄 Extrato CSV</button>
                <button onClick={() => { onExport('pdf'); setShowMobileMenu(false); }}>📑 Extrato PDF</button>

                <div className="mobile-menu-section-title">Ambiente</div>
                <button onClick={() => { onToggleDarkTheme(); setShowMobileMenu(false); }}>
                  {isDark ? '☀️ Tema Claro' : '🌙 Tema Escuro'}
                </button>
                <button onClick={() => { onOpenHelp(); setShowMobileMenu(false); }}>❓ Central de Ajuda</button>
                <button onClick={() => { logout(); setShowMobileMenu(false); }} className="logout-btn">🚪 Sair do Sistema</button>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
